import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import AdminHeader from "../../../Components/AdminHeader/AdminHeader";
import AdminNav from "../../../Components/AdminNav/AdminNav";

import AddImageComponent from "./AddImageComponent";
import AddCustomBodyComponent from "./AddCustomBodyComponent";
import Add4ImageGallery from "./Add4ImageGallery";
import AddGalleryComponent from "./AddGalleryComponent";
import ImageText from "./ImageText";
import TextImage from "./TextImage";
import TableComponent from "./TableComponent";
import QuoteText from "./QuoteText";
import TextQuote from "./TextQuote"; // ✅ new import
import { getCustomNews, updateCustomNews } from "../../../actions/NewsPages";
import { IMAGE_URL } from "../../../path";

interface FormDataState {
    title: string;
    subtitle: string;
    year_posted: string;
    image: File | null;
    imagePreview?: string;
}

interface SectionData {
    id: number;
    type:
        | "image"
        | "body"
        | "gallery4"
        | "gallery"
        | "imageText"
        | "textImage"
        | "table"
        | "quote"
        | "textQuote";
    bodyCopy?: string;
    quote?: string;
    tableData?: string[];
    imageFile?: File | null;
    caption?: string;
    isFullWidth?: boolean;
    images?: {
        id: number;
        file?: File | null;
        caption?: string;
        preview?: string;
    }[];
    title?: string;
    preview?: string;
}

function EditCustomArticle() {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const imgURL = IMAGE_URL;

    const user = location.state?.user;
    if (!user) navigate("/login");

    const [isCollapsed, setIsCollapsed] = useState(true);
    const [formData, setFormData] = useState<FormDataState>({
        title: "",
        subtitle: "",
        year_posted: "",
        image: null,
    });
    const [dynamicSections, setDynamicSections] = useState<SectionData[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!id) return;
        getCustomNews(Number(id))
            .then((response) => {
                const data = response.data;
                setFormData({
                    title: data.title || "",
                    subtitle: data.subtitle || "",
                    year_posted: data.date_posted || "",
                    image: null,
                    imagePreview: data.cover_image || "",
                });

                const sections: SectionData[] = data.components.map((comp: any, index: number) => {
                    const compData = comp.data || {};
                    const base: SectionData = {
                        id: comp.id || index,
                        type: comp.type,
                    };

                    switch (comp.type) {
                        case "body":
                            base.bodyCopy = compData?.body_copy || "";
                            break;

                        case "quote":
                        case "textQuote": // ✅ support both
                            base.quote = compData?.quote || "";
                            base.bodyCopy = compData?.body_copy || "";
                            break;

                        case "image":
                            base.caption = compData?.caption || "";
                            base.isFullWidth = compData?.is_full_width === 1;
                            base.imageFile = null;
                            base.preview = compData?.image_path || "";
                            break;

                        case "imageText":
                        case "textImage":
                            base.bodyCopy = compData?.body_copy || "";
                            base.isFullWidth = compData?.is_full_width === 1;
                            base.imageFile = null;
                            base.preview = compData?.image_path || "";
                            break;

                        case "table":
                            const tableData: string[] = [];
                            if (compData?.content1) tableData.push(compData.content1);
                            if (compData?.content2) tableData.push(compData.content2);
                            if (compData?.content3) tableData.push(compData.content3);
                            if (compData?.content4) tableData.push(compData.content4);
                            base.tableData = tableData;
                            break;

                        case "gallery4":
                            base.images = [
                                { id: 0, file: null, preview: compData?.image1_path || "" },
                                { id: 1, file: null, preview: compData?.image2_path || "" },
                                { id: 2, file: null, preview: compData?.image3_path || "" },
                                { id: 3, file: null, preview: compData?.image4_path || "" },
                            ];
                            break;

                        case "gallery":
                            base.title = compData?.title || "";
                            base.images = (compData?.images || []).map(
                                (img: any, idx: number) => ({
                                    id: idx,
                                    file: null,
                                    caption: img.caption || "",
                                    preview: img.image_path || "",
                                })
                            );
                            break;
                    }
                    return base;
                });

                setDynamicSections(sections);
            })
            .catch((err) => console.error("Error loading article:", err))
            .finally(() => setLoading(false));
    }, [id]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        if (type === "file") {
            const fileInput = e.target as HTMLInputElement;
            const file = fileInput.files?.[0] || null;
            setFormData((prev) => ({ ...prev, [name]: file }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const updateSection = (id: number, updatedData: Partial<SectionData>) => {
        setDynamicSections((prev) =>
            prev.map((section) =>
                section.id === id
                    ? {
                        ...section,
                        ...updatedData,
                        images: updatedData.images
                            ? updatedData.images.map((img, i) => ({
                                ...section.images?.[i],
                                ...img,
                            }))
                            : section.images,
                    }
                    : section
            )
        );
    };

    const addSection = (type: SectionData["type"]) => {
        const baseId = Date.now();
        if (type === "gallery4") {
            setDynamicSections((prev) => [
                ...prev,
                {
                    id: baseId,
                    type,
                    images: Array(4)
                        .fill(null)
                        .map((_, i) => ({ id: baseId + i, file: null, preview: "" })),
                },
            ]);
        } else if (type === "gallery") {
            setDynamicSections((prev) => [
                ...prev,
                {
                    id: baseId,
                    type,
                    title: "",
                    images: [{ id: baseId, file: null, caption: "", preview: "" }],
                },
            ]);
        } else if (type === "table") {
            setDynamicSections((prev) => [
                ...prev,
                { id: baseId, type, tableData: Array(2).fill("") },
            ]);
        } else if (type === "quote" || type === "textQuote") { // ✅
            setDynamicSections((prev) => [
                ...prev,
                { id: baseId, type, quote: "", bodyCopy: "" },
            ]);
        } else {
            setDynamicSections((prev) => [...prev, { id: baseId, type }]);
        }
    };

    const removeSection = (id: number) => {
        setDynamicSections((prev) => prev.filter((section) => section.id !== id));
    };

    const handleSave = async (isDraft: boolean) => {
        try {
            const data = new FormData();
            data.append("title", formData.title);
            data.append("subtitle", formData.subtitle);
            data.append("year_posted", formData.year_posted);
            data.append("draft", isDraft ? "1" : "0");
            if (formData.image) data.append("image", formData.image);

            dynamicSections.forEach((section, index) => {
                const comp: any = { type: section.type, display_order: index, data: {} };

                switch (section.type) {
                    case "body":
                        comp.data.body_copy = section.bodyCopy || "";
                        console.log(comp.data.body_copy);
                        break;

                    case "quote":
                    case "textQuote": // ✅ handle both
                        comp.data.quote = section.quote || "";
                        comp.data.body_copy = section.bodyCopy || "";
                        break;

                    case "table":
                        comp.data.body_copy = section.tableData || [];
                        break;

                    case "image":
                    case "imageText":
                    case "textImage":
                        if (section.imageFile) {
                            data.append(`components_files[${index}]`, section.imageFile);
                            comp.data.image_path = null;
                        } else if (section.preview) {
                            comp.data.image_path = section.preview.replace(IMAGE_URL, "");
                        } else {
                            comp.data.image_path = null;
                        }
                        comp.data.caption = section.caption || "";
                        comp.data.is_full_width = section.isFullWidth ? 1 : 0;

                        if (section.type === "imageText" || section.type === "textImage") {
                            comp.data.body_copy = section.bodyCopy || "";
                        }
                        break;

                    case "gallery4":
                    case "gallery":
                        comp.data.title = section.title || "";
                        comp.data.images =
                            section.images?.map((img, imgIndex) => {
                                if (img.file) {
                                    data.append(`components_files[${index}][${imgIndex}]`, img.file);
                                    return { path: null, caption: img.caption || "" };
                                } else if (img.preview) {
                                    return {
                                        path: img.preview.replace(IMAGE_URL, ""),
                                        caption: img.caption || "",
                                    };
                                } else {
                                    return { path: null, caption: img.caption || "" };
                                }
                            }) || [];
                        break;
                }

                data.append(`components_meta[${index}]`, JSON.stringify(comp));
            });

            const response = await updateCustomNews(Number(id), data);
            if (response.data.success) {
                alert(isDraft ? "Draft saved!" : "Article updated successfully!");
                navigate("/dashboard/news/list", { state: { user } });
            }
        } catch (error) {
            console.error(error);
            alert("Error updating article");
        }
    };

    if (loading) return <p>Loading article...</p>;

    return (
        <div>
            <AdminHeader isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} user={user} />
            <AdminNav collapsed={isCollapsed} setCollapsed={setIsCollapsed} user={user} />

            <div className={`main-admin-page ${isCollapsed ? "collapsed" : "expanded"}`}>
                <form style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                    <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Title"
                    />

                    <textarea
                        name="subtitle"
                        value={formData.subtitle}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Subtitle"
                    />

                    <input
                        type="date"
                        name="year_posted"
                        value={formData.year_posted}
                        onChange={handleInputChange}
                        className="form-control"
                    />

                    <div>
                        <label>Cover Image</label><br />

                        <input
                            type="file"
                            name="image"
                            className="form-control"
                            onChange={handleInputChange}
                        />

                        {(formData.image || formData.imagePreview) && (
                            <img
                                src={
                                    formData.image
                                        ? URL.createObjectURL(formData.image)
                                        : `${IMAGE_URL}${formData.imagePreview}`
                                }
                                alt="Cover"
                                style={{
                                    width: "70%",
                                    marginTop: "10px",
                                    objectFit: "cover",
                                    borderRadius: "5px",
                                }}
                            />
                        )}
                    </div>
                    {dynamicSections.map((section) => {
                        switch (section.type) {
                            case "image":
                                return (
                                    <AddImageComponent
                                        key={section.id}
                                        section={{
                                            ...section,
                                            preview: section.preview
                                                ? `${imgURL}${section.preview}`
                                                : undefined,
                                        }}
                                        onChange={updateSection}
                                        onClose={() => removeSection(section.id)}
                                    />
                                );
                            case "body":
                                return (
                                    <AddCustomBodyComponent
                                        key={section.id}
                                        section={section}
                                        onChange={updateSection}
                                        onClose={() => removeSection(section.id)}
                                    />
                                );
                            case "quote":
                                return (
                                    <QuoteText
                                        key={section.id}
                                        section={section}
                                        onChange={updateSection}
                                        onClose={() => removeSection(section.id)}
                                    />
                                );
                            case "textQuote": // ✅ render TextQuote component
                                return (
                                    <TextQuote
                                        key={section.id}
                                        section={section}
                                        onChange={updateSection}
                                        onClose={() => removeSection(section.id)}
                                    />
                                );
                            case "gallery4":
                                return (
                                    <Add4ImageGallery
                                        key={section.id}
                                        section={section}
                                        onChange={updateSection}
                                        onClose={() => removeSection(section.id)}
                                    />
                                );
                            case "gallery":
                                return (
                                    <AddGalleryComponent
                                        key={section.id}
                                        section={section}
                                        onChange={updateSection}
                                        onClose={() => removeSection(section.id)}
                                    />
                                );
                            case "imageText":
                                return (
                                    <ImageText
                                        key={section.id}
                                        section={{
                                            ...section,
                                            imagePath: section.preview
                                                ? section.preview.replace(imgURL, "")
                                                : undefined,
                                        }}
                                        onChange={updateSection}
                                        onClose={() => removeSection(section.id)}
                                        baseUrl={imgURL}
                                    />
                                );
                            case "textImage":
                                return (
                                    <TextImage
                                        key={section.id}
                                        section={{
                                            ...section,
                                            imagePath: section.preview
                                                ? section.preview
                                                : undefined,
                                        }}
                                        onChange={updateSection}
                                        onClose={() => removeSection(section.id)}
                                        baseUrl={imgURL}
                                    />
                                );
                            case "table":
                                return (
                                    <TableComponent
                                        key={section.id}
                                        sectionId={section.id}
                                        initialData={section.tableData || []}
                                        onChange={(id, updatedData) =>
                                            updateSection(id, { tableData: updatedData.bodyCopy })
                                        }
                                        onClose={() => removeSection(section.id)}
                                    />
                                );
                        }
                    })}

                    <div
                        style={{
                            display: "flex",
                            gap: 10,
                            justifyContent: "center",
                            marginTop: 20,
                        }}
                    >
                        <button type="button" onClick={() => addSection("image")}>
                            Add Image
                        </button>
                        <button type="button" onClick={() => addSection("body")}>
                            Add Body
                        </button>
                        <button type="button" onClick={() => addSection("gallery4")}>
                            Add 4-Image Gallery
                        </button>
                        <button type="button" onClick={() => addSection("gallery")}>
                            Add Gallery
                        </button>
                        <button type="button" onClick={() => addSection("imageText")}>
                            Add Image then Text
                        </button>
                        <button type="button" onClick={() => addSection("textImage")}>
                            Add Text then Image
                        </button>
                        <button type="button" onClick={() => addSection("table")}>
                            Add Table
                        </button>
                        <button type="button" onClick={() => addSection("quote")}>
                            Add Quote then Text
                        </button>
                        <button type="button" onClick={() => addSection("textQuote")}>
                            Add Text then Quote
                        </button> {/* ✅ new button */}
                    </div>

                    <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 10 }}>
                        <button type="button" onClick={() => handleSave(true)}>
                            Save Draft
                        </button>
                        <button type="button" onClick={() => handleSave(false)}>
                            Update Article
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default EditCustomArticle;
